<template>
  <button
    class="btn btn-warning btn-sm"
    data-testid="clipboard-copy-btn"
    @click="handleClipboardCopy"
    :disabled="loading">
    <ProgressSpinner v-if="loading" style="width: 1rem; height: 1rem" strokeWidth="4" />
    <i v-else class="pi pi-copy"></i> 
    {{ text || 'Copiar' }}
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressSpinner from 'primevue/progressspinner';
import { useDateFormatter } from '../../composables/useDateFormatter';

const props = defineProps<{
  text?: string;
  config: Record<string, any>;
  data?: any[];
  getFilteredData?: () => any[];
}>();

const emit = defineEmits<{
  (e: 'clipboard-copy'): void;
  (e: 'clipboard-error', error: any): void;
}>();

// Get i18n for formatting
const { n } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

const loading = ref(false);

const handleClipboardCopy = async () => {
  if (loading.value) return;
  
  try {
    loading.value = true;
    
    await copyToClipboard();
    
    // Emit success event
    emit('clipboard-copy');
    
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    emit('clipboard-error', error);
  } finally {
    loading.value = false;
  }
};

const copyToClipboard = async (): Promise<void> => {
  try {
    // Get filtered data or all data
    const filtered = props.getFilteredData?.() ?? [];
    const dataToExport = filtered.length > 0 ? filtered : (props.data ?? []);
    
    if (!dataToExport || dataToExport.length === 0) {
      throw new Error('No data to copy');
    }
    
    // Get visible columns
    const columns = props.config.columns.filter((col: any) => col.visible !== false);

    // Create table headers
    const headers = columns.map((column: any) => column.title || column.data);

    // Create table rows
    const rows = dataToExport.map((row: any) => {
      return columns.map((column: any) => {
        let cellValue = row[column.data];
        
        // Format cell value based on render type
        if (column.renderType) {
          cellValue = formatCellValueForClipboard(cellValue, column.renderType);
        }
        
        // Convert to string and clean up
        const cellText = String(cellValue || '').trim();
        
        return cellText;
      });
    });
    
    // Create tab-separated content (works well with Excel when pasted)
    const tabSeparatedContent = [headers, ...rows]
      .map(row => row.join('\t'))
      .join('\n');
    
    // Try to use the modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(tabSeparatedContent);
    } else {
      // Fallback for older browsers or non-secure contexts
      await fallbackCopyToClipboard(tabSeparatedContent);
    }
    
  } catch (error) {
    console.error('Clipboard Copy Error:', error);
    throw new Error(`Failed to copy to clipboard: ${error.message}`);
  }
};

const fallbackCopyToClipboard = (text: any) => {
  return new Promise<void>((resolve, reject) => {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    
    try {
      // Select and copy the text
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      if (successful) {
        resolve(undefined);
      } else {
        reject(new Error('Copy command failed'));
      }
    } catch (err) {
      reject(err);
    } finally {
      // Clean up
      document.body.removeChild(textArea);
    }
  });
};

const formatCellValueForClipboard = (value: any, renderType: any) => {
  if (value === null || value === undefined) return '';

  switch (renderType) {
    case 'currency':
      const currencyValue = parseFloat(value) || 0;
      return n(currencyValue, 'currency');
    case 'number':
      const numberValue = parseFloat(value) || 0;
      return n(numberValue, 'decimal');
    case 'percentage':
      const percentageValue = parseFloat(value) || 0;
      return n(percentageValue / 100, 'percent');
    case 'date':
      if (!value) return '';
      const date = new Date(value);
      return formatDate(date);
    case 'datetime':
      if (!value) return '';
      const datetime = new Date(value);
      return formatDateTime(datetime);
    case 'yes_no':
      return value ? 'Sim' : 'Não';
    case 'active_inactive':
      return value ? 'Ativo' : 'Inativo';
    default:
      return String(value);
  }
};
</script>
